import i18n from 'i18next';
import type { Resource, ResourceKey } from 'i18next';
import { initReactI18next } from 'react-i18next';

type SupportedLanguage = 'fi' | 'en' | 'sv';

const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['fi', 'en', 'sv'];

// Every src/locales/<lang>/<namespace>.json is bundled and registered
// automatically, so a new namespace can never be left unwired for en/sv.
// `import: 'default'` is required — without it each namespace registers
// as `{ default: {...} }` and every key lookup breaks.
const localeModules = import.meta.glob('./locales/*/*.json', {
  eager: true,
  import: 'default'
}) as Record<string, ResourceKey>;

const resources: Resource = { fi: {}, en: {}, sv: {} };

for (const [path, translations] of Object.entries(localeModules)) {
  const match = path.match(/^\.\/locales\/([^/]+)\/(.+)\.json$/);
  if (!match) continue;
  const [, lang, namespace] = match;
  if (!SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage)) continue;
  resources[lang][namespace] = translations;
}

const ns = Object.keys(resources.fi);

const getSavedLanguage = (): SupportedLanguage => {
  const saved = localStorage.getItem('preferredLanguage');
  if (saved && SUPPORTED_LANGUAGES.includes(saved as SupportedLanguage)) {
    return saved as SupportedLanguage;
  }

  const browserLang = navigator.language.toLowerCase();
  if (browserLang.startsWith('fi')) return 'fi';
  if (browserLang.startsWith('sv')) return 'sv';
  if (browserLang.startsWith('en')) return 'en';

  return 'fi';
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getSavedLanguage(),
    fallbackLng: 'fi',

    ns,
    defaultNS: 'common',
    fallbackNS: 'common',

    interpolation: {
      escapeValue: false
    },

    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p']
    }
  });

export const syncLanguageFromUser = (user: { preferredLanguage?: string | null }) => {
  const lang = user.preferredLanguage
  if (lang && SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage) && i18n.language !== lang) {
    i18n.changeLanguage(lang)
    localStorage.setItem('preferredLanguage', lang)
  }
}

export default i18n;
